import './index.scss';
import React from 'react';
import { Modal, Form, Input, Button } from 'semantic-ui/semantic-ui-imports';
import { toast } from 'react-semantic-toasts';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/auth';
import { Project } from '../../Types/Project';
import { createProject, updateProject } from '../../services/projects';
import { PromptToSignInContext } from '../../contexts/promptToSignIn';

interface Props {
  show: boolean;
  initial?: Project;
  onHide?: (obj: Project | null) => void;
  btnText?: string | undefined;
}
const classId: string = 'create-or-update-project';

const CreateOrUpdateProject = ({ initial, onHide, btnText, show }: Props) => {
  const [obj, setObj] = React.useState<Project>(initial || ({} as Project));
  const [loading, setLoading] = React.useState<boolean>(false);
  const [isValid, setIsValid] = React.useState<boolean>(false);
  const { isAuthenticated } = useAuth();
  const [submitted, setSubmitted] = React.useState<boolean>(false);
  const { promptToSignIn } = React.useContext(PromptToSignInContext);

  const { t } = useTranslation();

  const updateField = (key: string, value: any) => {
    setObj({ ...obj, [key]: value });
  };

  React.useEffect(() => {
    setIsValid(!!obj.name);
  }, [obj.name]);

  const onClose = (p: Project | null) => {
    if (onHide) {
      onHide(p);
    }
  };

  const save = async () => {
    if (!isAuthenticated) {
      promptToSignIn();
      return;
    }

    setSubmitted(true);
    if (!isValid) {
      return;
    }

    setLoading(true);
    let updatedObj: Project | null;

    try {
      if (initial?.id) {
        updatedObj = await updateProject(obj);
      } else {
        updatedObj = await createProject(obj);
      }
      onClose(updatedObj);
    } catch (err) {
      toast({
        type: 'error',
        title: t('common.somethingWentWrong'),
        time: 4000,
        size: 'small',
      });
    }
    setLoading(false);
  };

  return (
    <Modal onClose={() => onClose(obj)} open={show} className={`${classId}`}>
      <Modal.Header className={`${classId}__header`}>
        {initial
          ? t('createOrUpdateProject.udpdateTitle')
          : t('createOrUpdateProject.createTitle')}
      </Modal.Header>
      <Modal.Content className={`${classId}__content`}>
        <Form onSubmit={() => save()}>
          <Form.Field
            control={Input}
            label={t('createOrUpdateProject.nameLabel')}
            name="name"
            placeholder={t('common.inputWrite')}
            defaultValue={obj.name}
            onChange={(e: any) => updateField('name', e.target.value)}
            required
            error={
              submitted &&
              !obj?.name && {
                content: t('createOrUpdateProject.nameError'),
                pointing: 'below',
              }
            }
          />
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={() => onClose(obj)}>{t('common.cancel')}</Button>
        <Button primary loading={loading} onClick={() => save()}>
          {btnText || t('common.save')}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

CreateOrUpdateProject.defaultProps = {
  onHide: () => {},
  btnText: undefined,
  initial: undefined,
};

export default CreateOrUpdateProject;
